import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Inbox,
  MailOpen,
  MessageSquareReply,
  Moon,
  PlugZap,
  Star,
  UserCheck,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import { PriorityPill } from "@/components/PriorityPill";
import { UnreadBadge } from "@/components/UnreadBadge";
import { PresencePanel } from "@/components/PresencePanel";
import { UserHome } from "@/components/UserHome";
import { useIsAgent, useSession } from "@/components/session-provider";
import {
  ageOf,
  getDashboard,
  getReference,
  useStatusLabel,
  statusTone,
  unreadFor,
  type Dashboard,
  type ReferenceTeam,
} from "@/lib/tickets";
import { TeamScopeButton } from "@/components/tickets/TeamScopeButton";
import {
  ALL_TEAMS,
  HOME_TEAM_SCOPE_KEY,
  QUEUE_TEAM_SCOPE_KEY,
  rememberTeamScope,
  useTeamScope,
} from "@/components/tickets/team-scope";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/")({
  component: HomeRoute,
});

function HomeRoute() {
  // Same route, two audiences, decided by a permission the server issued. The mock used
  // a localStorage flag here, which is how anyone could reach the agent console (D1).
  const isAgent = useIsAgent();
  return isAgent ? <AgentHome /> : <UserHome />;
}

function AgentHome() {
  const { user } = useSession();
  const statusLabelFor = useStatusLabel();
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Which desk this screen is showing — the queue's own team scope (D-116),
  // extended here with its own storage key so the two never silently share a value.
  // -------------------------------------------------------------------------
  const [teams, setTeams] = useState<ReferenceTeam[] | null>(null);
  const { scope: storedTeamScope, setScope: setTeamScope } = useTeamScope(
    HOME_TEAM_SCOPE_KEY,
    user?.readableTeamIds ?? [],
  );
  const [teamId, setTeamId] = useState<string | null>(null);

  useEffect(() => {
    void getReference()
      .then((reference) => setTeams(reference.teams))
      .catch(() => setTeams([]));
  }, []);

  // Seeded once, on arrival, from the stored choice — never re-applied behind the reader's
  // back once they have picked something in this session.
  const seededTeamScope = useRef(false);
  useEffect(() => {
    if (seededTeamScope.current || storedTeamScope === null) return;
    seededTeamScope.current = true;
    setTeamId(storedTeamScope);
  }, [storedTeamScope]);

  useEffect(() => {
    let cancelled = false;

    // Cleared rather than left stale: switching teams and briefly showing the OLD team's
    // figures under the new team's label is a worse read than a moment of skeletons.
    setData(null);
    setError(null);

    void (async () => {
      try {
        const result = await getDashboard(teamId ?? undefined);
        if (!cancelled) setData(result);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof ApiError ? e.message : "Could not load the dashboard.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const firstName = user?.displayName?.split(" ")[0] ?? "there";

  // ⚠️ Cards whose figure was counted under this screen's team scope carry that scope into the
  // queue they open: `team` in the URL (the queue's single filter, D-116) plus the queue's
  // remembered scope, so the list and the number come from the same desk. Personal figures
  // (unread, your own requests, ratings) are not team-scoped here and do not use these.
  const teamSearch = teamId ? { team: teamId } : {};
  const carryScope = () => rememberTeamScope(QUEUE_TEAM_SCOPE_KEY, teamId ?? ALL_TEAMS);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Good to see you, {firstName}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Everything below is scoped to what you can see.
          </p>
        </div>

        {/* ⚠️ Same control the queue uses, its own storage key, and it writes no URL here —
            this screen has nothing to share as a link the way a filtered queue does. Drawn
            only where there is a choice to make (2+ readable teams); TeamScopeButton itself
            returns null otherwise. */}
        <TeamScopeButton
          teams={teams}
          readableTeamIds={user?.readableTeamIds ?? []}
          value={teamId}
          onChange={(scope) => {
            setTeamScope(scope);
            setTeamId(scope === ALL_TEAMS ? null : scope);
          }}
        />
      </header>

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
        >
          {error}
        </div>
      )}

      {/* Two across on a phone rather than one — these are short numbers and a single
          column turns the dashboard into a scroll. */}
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label="Open"
          value={data?.openCount}
          icon={Inbox}
          to="/tickets"
          search={{ view: "All open", ...teamSearch }}
          onNavigate={carryScope}
        />
        <Stat
          label="Assigned to me"
          value={data?.assignedToMeCount}
          icon={UserCheck}
          to="/tickets"
          search={{ view: "My tickets", ...teamSearch }}
          onNavigate={carryScope}
        />
        <Stat
          label="Unassigned"
          value={data?.unassignedCount}
          icon={Inbox}
          to="/tickets"
          search={{ view: "Unassigned", ...teamSearch }}
          onNavigate={carryScope}
          tone={data && data.unassignedCount > 0 ? "warning" : undefined}
        />
        <Stat
          label="Urgent or above"
          value={data?.urgentCount}
          icon={AlertTriangle}
          to="/tickets"
          search={{ view: "Urgent", ...teamSearch }}
          onNavigate={carryScope}
          tone={data && data.urgentCount > 0 ? "danger" : undefined}
        />
      </div>

      {/* The two buckets that do not appear anywhere else in the product.

          ⚠️ Both are null for anybody who does not work requests, because the SERVER did not
          send them (see DashboardResponse) — not because this branch hid them. Rendering on
          `!== null` rather than on a permission is deliberate: one decision, taken once,
          server-side. */}
      {data && (data.awaitingDeskReplyCount !== null || data.goneQuietCount !== null) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {/* ⚠️ Both land on the SAME predicate that produced the number — `TicketAttention`,
              read by this count and by the queue's filter. The first cut of this screen had
              one tile that linked nowhere and one that linked to every `AwaitingUser` request
              rather than the quiet ones, so a tile saying 3 opened a list of 11 and there was
              no way to tell which figure was wrong. */}
          {data.awaitingDeskReplyCount !== null && (
            <Link
              to="/tickets"
              search={{ attention: "AwaitingDeskReply", ...teamSearch }}
              onClick={carryScope}
              className={cn(
                "rounded-xl border bg-card p-4 transition hover:border-primary/40",
                data.awaitingDeskReplyCount > 0 ? "border-warning/40" : "border-border",
              )}
            >
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <MessageSquareReply className="h-3.5 w-3.5" aria-hidden />
                Waiting on a reply from us
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.awaitingDeskReplyCount}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">The person who asked spoke last</p>
            </Link>
          )}

          {/* ⚠️ Its own link rather than a line of text inside the tile above, because it is
              its own set: the "My tickets" view carries `mine`, so this lands on the same
              bucket narrowed to the caller's own work — which is what the number counts. A
              figure printed inside a tile that opens a wider list is the defect this whole
              change is fixing. */}
          {data.awaitingYourReplyCount !== null && data.awaitingYourReplyCount > 0 && (
            <Link
              to="/tickets"
              search={{ view: "My tickets", attention: "AwaitingDeskReply", ...teamSearch }}
              onClick={carryScope}
              className="rounded-xl border border-border bg-card p-4 transition hover:border-primary/40 sm:col-span-2"
            >
              <p className="text-sm">
                <span className="font-medium tabular-nums">{data.awaitingYourReplyCount}</span> of
                those {data.awaitingYourReplyCount === 1 ? "is" : "are"} assigned to you
              </p>
            </Link>
          )}

          {/* Drawn only when there is something to do about it, unlike the two beside it.
              Those describe an ordinary steady state a desk always has some of; this one is
              empty on every desk that has connected no application, and a permanent zero
              under a heading nobody recognises is a tile that has to be explained rather
              than read. */}
          {(data.awaitingRequesterIdentificationCount ?? 0) > 0 && (
            <Link
              to="/tickets"
              search={{ attention: "AwaitingRequesterIdentification", ...teamSearch }}
              onClick={carryScope}
              className="rounded-xl border border-warning/40 bg-card p-4 transition hover:border-primary/40"
            >
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <PlugZap className="h-3.5 w-3.5" aria-hidden />
                Needs a requester
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.awaitingRequesterIdentificationCount}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Raised by an application that could not say who for
              </p>
            </Link>
          )}

          {data.goneQuietCount !== null && (
            <Link
              to="/tickets"
              search={{ attention: "GoneQuiet", ...teamSearch }}
              onClick={carryScope}
              className={cn(
                "rounded-xl border bg-card p-4 transition hover:border-primary/40",
                data.goneQuietCount > 0 ? "border-warning/40" : "border-border",
              )}
            >
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Moon className="h-3.5 w-3.5" aria-hidden />
                Gone quiet
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{data.goneQuietCount}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Waiting on the requester for 5 days or more
              </p>
            </Link>
          )}
        </div>
      )}

      {/* ⚠️ Renders nothing unless the SERVER sent the section — it is null for anybody who is
          neither desk staff nor an administrator, so there is no permission test here and
          there must not be one. */}
      <div className="mt-4">
        <PresencePanel presence={data?.presence ?? null} />
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Resolved in the last 7 days</p>
          <p className="mt-1 flex items-center gap-2 text-2xl font-semibold tabular-nums">
            <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />
            {data?.resolvedThisWeekCount ?? "—"}
          </p>
        </div>

        {/* ⚠️ NARROWED TO THEIR OWN, which is what the number counts. This linked to a bare
            `status=AwaitingUser`, so a tile headed "your own requests" and reading 2 opened
            every awaiting-reply request in the reader's teams — the D-70 defect, surviving on
            the one tile that change did not reach.

            The requester filter resolves to the caller's own id server-side and is accepted
            for exactly that case without `user.manage`, so this narrows to the same set the
            count was taken over rather than approximating it. */}
        {data && data.awaitingYouCount > 0 && user && (
          <Link
            to="/tickets"
            search={{ status: "AwaitingUser", requester: user.userId }}
            className="rounded-xl border border-warning/40 bg-warning/5 p-4 transition hover:border-warning"
          >
            <p className="text-xs text-muted-foreground">Your own requests awaiting a reply</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{data.awaitingYouCount}</p>
          </Link>
        )}
      </div>

      {/* Somebody who works requests also raises them. Both of these are about their own. */}
      {data && data.unreadTicketCount > 0 && (
        // ⚠️ The attention filter, not the requester screen's tab — an agent following this
        // lands on the agent queue. Same server predicate either way, and it counts the same
        // rows as the bell, so the number opens a list of exactly that many.
        <Link
          to="/tickets"
          search={{ attention: "Unread" }}
          className="mt-4 flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 p-3 text-sm transition hover:border-primary"
        >
          <MailOpen className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          {data.unreadTicketCount === 1
            ? "One request has an update you haven't read"
            : `${data.unreadTicketCount} requests have updates you haven't read`}
        </Link>
      )}

      {data && (data.awaitingYourRatingCount ?? 0) > 0 && (
        // ⚠️ The attention filter rather than the requester screen's tab: an agent following
        // this lands on the AGENT queue, which has its own tab vocabulary. Same server
        // predicate either way — AwaitingRating resolves to the caller's own requests.
        <Link
          to="/tickets"
          search={{ attention: "AwaitingRating" }}
          className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-card p-3 text-sm transition hover:border-primary/40"
        >
          <Star className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          {data.awaitingYourRatingCount === 1
            ? "One of your own resolved requests is waiting for your feedback"
            : `${data.awaitingYourRatingCount} of your own resolved requests are waiting for your feedback`}
        </Link>
      )}

      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium">Recently updated</h2>
          <Link
            to="/tickets"
            search={teamSearch}
            onClick={carryScope}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Open the queue
          </Link>
        </div>

        {data === null ? (
          <div className="mt-3 space-y-2" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl border border-border bg-card" />
            ))}
          </div>
        ) : data.recent.length === 0 ? (
          <p className="mt-3 rounded-2xl border border-dashed border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
            The queue is clear.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {data.recent.map((t) => (
              <li key={t.number}>
                <Link
                  to="/tickets/$id"
                  params={{ id: t.number }}
                  className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-3 transition hover:border-primary/40"
                >
                  {/* No face on a list row. A picture belongs where you are looking at one
                      person — the ticket itself, or their profile — not repeated down a
                      list whose subject is the request rather than the requester. */}
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="min-w-0">
                      <span className="line-clamp-1 block font-medium">{t.subject}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        #{t.displayNumber} · {t.requesterName} · {ageOf(t.updatedAtUtc)} ago
                      </span>
                    </span>
                  </span>

                  <span className="flex shrink-0 items-center gap-2">
                    <UnreadBadge count={unreadFor(data, t.number)} />
                    <PriorityPill p={t.priority} />
                    <span
                      className={cn(
                        "hidden rounded-md px-2 py-0.5 text-xs font-medium sm:inline",
                        statusTone[t.status],
                      )}
                    >
                      {statusLabelFor(t.status)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  to,
  search,
  onNavigate,
  tone,
}: {
  label: string;
  value: number | undefined;
  icon: typeof Inbox;
  to: string;
  search?: { view: string; team?: string };
  onNavigate?: () => void;
  tone?: "warning" | "danger";
}) {
  return (
    <Link
      to={to}
      search={search}
      onClick={onNavigate}
      className={cn(
        "rounded-xl border bg-card p-4 transition hover:border-primary/40",
        tone === "danger"
          ? "border-destructive/40"
          : tone === "warning"
            ? "border-warning/40"
            : "border-border",
      )}
    >
      <span className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </span>
      <span className="mt-1 block text-2xl font-semibold tabular-nums">
        {value === undefined ? "—" : value}
      </span>
    </Link>
  );
}
